import {
  getApiErrorMessage,
  moveUsersToOgGroupApi,
} from "apis/adminServices/userManagmentService";
import { OgUserGroup, User } from "./type";
import { NO_OG_GROUP_LABEL } from "./constants";

export interface MoveResult {
  succeeded: number;
  failed: number;
  /** Ready for a toast — the API's own wording whenever there is just one. */
  message: string;
  toastType?: "success" | "error" | "info";
}

/** Check if a user is already a member of the target group */
export const isUserInOgGroup = (
  user: User,
  targetRoleId: number | null,
  groups: OgUserGroup[]
): boolean => {
  const roles = user.userRoles || [];
  const normalize = (val?: string | null) =>
    (val ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

  if (targetRoleId === null) {
    // General Users = user has no OG roles (only "user" or empty)
    const ogRoles = roles.filter((r) => normalize(r) !== "user");
    return ogRoles.length === 0;
  }

  const targetGroup = groups.find((g) => g.id === targetRoleId);
  if (!targetGroup) return false;

  const targetNormalized = normalize(targetGroup.roleName);
  return roles.some((r) => normalize(r) === targetNormalized);
};

/**
 * Move every selected user into `roleId` (or out of all OG groups when it is
 * null) using a single API call with `{ userIds: number[], roleId: number | null }`.
 *
 * Validates whether users already belong to the destination group and returns
 * an appropriate validation message.
 */
export const moveUsersToOgGroup = async (
  users: User[],
  roleId: number | null,
  groups?: OgUserGroup[]
): Promise<MoveResult> => {
  // Restrict blocked users from being moved to another user role
  const blockedUsers = users.filter(
    (user) => user.status?.toLowerCase() === "blocked"
  );
  if (blockedUsers.length > 0) {
    return {
      succeeded: 0,
      failed: blockedUsers.length,
      toastType: "error",
      message: "Blocked users cannot be moved to another user role.",
    };
  }

  const targetGroup =
    roleId !== null
      ? groups?.find((g) => Number(g.id) === Number(roleId))
      : null;
  const groupLabel =
    targetGroup?.roleName || (roleId === null ? NO_OG_GROUP_LABEL : "OG user group");

  // Identify users already in the destination group
  const alreadyInGroup = groups
    ? users.filter((user) => isUserInOgGroup(user, roleId, groups))
    : [];
  const usersToMove = groups
    ? users.filter((user) => !isUserInOgGroup(user, roleId, groups))
    : users;

  if (usersToMove.length === 0 && alreadyInGroup.length > 0) {
    const msg =
      users.length === 1
        ? `User is already added to ${groupLabel}.`
        : `Selected user(s) are already added to ${groupLabel}.`;
    return {
      succeeded: 0,
      failed: 0,
      toastType: "info",
      message: msg,
    };
  }

  try {
    const userIds = usersToMove.map((user) => user.userId);
    const response = await moveUsersToOgGroupApi(userIds, roleId);
    const val = response?.data || response;
    const backendMessage = val?.message || val?.Message;
    const isSuccess = val?.isSuccess ?? val?.IsSuccess ?? true;

    if (!isSuccess || (backendMessage && /already/i.test(backendMessage))) {
      return {
        succeeded: 0,
        failed: 0,
        toastType: "info",
        message: backendMessage || `User(s) already added to ${groupLabel}.`,
      };
    }

    const tokens =
      Number(
        val?.data?.tokensGranted ??
          val?.Data?.TokensGranted ??
          val?.tokensGranted
      ) || 0;

    let message: string;
    if (usersToMove.length === 1) {
      if (tokens > 0) {
        message = `User moved to ${groupLabel} and ${tokens} token(s) credited.`;
      } else if (roleId === null) {
        message = "User removed successfully.";
      } else {
        message = `User moved to ${groupLabel}.`;
      }
    } else {
      if (tokens > 0) {
        message = `${usersToMove.length} user(s) moved to ${groupLabel} and ${tokens} token(s) credited.`;
      } else if (roleId === null) {
        message = `${usersToMove.length} user(s) removed successfully.`;
      } else {
        message = `${usersToMove.length} user(s) moved to ${groupLabel}.`;
      }
    }

    if (alreadyInGroup.length > 0) {
      message += ` (${alreadyInGroup.length} already in ${groupLabel})`;
    }

    return {
      succeeded: usersToMove.length,
      failed: 0,
      toastType: "success",
      message,
    };
  } catch (error) {
    const errorMsg = getApiErrorMessage(
      error,
      usersToMove.length === 1
        ? "Failed to move the user."
        : "Failed to move users."
    );
    // The backend's own wording just says "this OG user group" — name the
    // group the admin actually picked instead.
    const message = /user slot/i.test(errorMsg)
      ? `Unable to assign the ${
          usersToMove.length === 1 ? "user" : "users"
        } because there are no available user slots in ${groupLabel}.`
      : errorMsg;
    return {
      succeeded: 0,
      failed: usersToMove.length,
      toastType: "error",
      message,
    };
  }
};
